import {computed, observable, makeObservable} from "mobx";
import type {AppState} from "../state/AppState";
import {XyiconFeature} from "../../generated/api/base";
import {ObjectUtils} from "../../utils/data/ObjectUtils";
import type {PortDataDto, XyiconDto, Dimensions} from "../../generated/api/base";
import type {SpaceItemType} from "../../ui/modules/space/spaceeditor/logic3d/managers/spaceitems/ItemManager";
import type {OrgSearchBoundarySpaceMapDto, OrgSearchResponseDto} from "../../generated/api/search";
import type {Catalog} from "./Catalog";
import type {Link} from "./Link";
import type {Space} from "./Space";
import type {IModel, IModelData, ISpaceItemModel} from "./Model";

export class Xyicon implements ISpaceItemModel, IXyiconMinimumData, IModel, XyiconDto {
	public static search(xyicon: Xyicon, search = "") {
		if (!search) {
			return true;
		}

		search = search.toLowerCase();
		const refId = xyicon.refId.toLowerCase();

		if (refId.includes(search)) {
			return true;
		}

		return false;
	}

	public readonly ownFeature = XyiconFeature.Xyicon;

	private _data: XyiconDto;
	private _appState: AppState;

	constructor(data: XyiconDto, appState: AppState) {
		makeObservable<this, "_data" | "metaData">(this, {
			_data: observable,
			id: computed,
			refId: computed,
			typeId: computed,
			spaceId: computed,
			space: computed,
			spaceTypeId: computed,
			spaceName: computed,
			catalogId: computed,
			iconX: computed,
			iconY: computed,
			iconZ: computed,
			position: computed,
			orientation: computed,
			iconCategory: computed,
			fieldData: computed,
			lastModifiedBy: computed,
			lastModifiedAt: computed,
			catalog: computed,
			type: computed,
			typeName: computed,
			embeddedXyicons: computed,
			parentXyicon: computed,
			parentXyiconRefId: computed,
			tempRefId: computed,
			metaData: computed,
			model: computed,
			icon: computed,
			thumbnail: computed,
			backgroundTransform: computed,
			isEmbedded: computed,
			isExternal: computed,
			portData: computed,
			isUnplotted: computed,
			data: computed,
		});
		this._appState = appState;
		this.applyData(data);
	}

	public applyData(data: Partial<XyiconDto>) {
		if (typeof data.settings === "string") {
			data.settings = JSON.parse(data.settings);
		}

		this._data = {
			...this._data,
			...data,
		};

		this._data.fieldData = this._data.fieldData || {};

		if (!this._data.portData) {
			this._data.portData = [];
		}

		if (!this._data.settings) {
			this._data.settings = {};
		}
	}

	public applyUpdate(data: IXyiconUpdate) {
		if (data.xyiconIDList.includes(this.id)) {
			this.updateLastModifiedData(data);
		}
	}

	public updateLastModifiedData(data: IModelData) {
		this._data.lastModifiedAt = data.lastModifiedAt ?? this._data.lastModifiedAt;
		this._data.lastModifiedBy = data.lastModifiedBy ?? this._data.lastModifiedBy;
	}

	public addPortData(portDataParam: PortDataDto) {
		const portLabelObject = this._data.portData.find((portDataLabel: PortDataDto) => portDataLabel.id === portDataParam.id);

		if (portLabelObject) {
			portLabelObject.label = portDataParam.label;
		} else {
			this._data.portData.push({...portDataParam});
		}
	}

	public get settings() {
		return this._data.settings || {};
	}

	public get isFlippedX(): boolean {
		return !!this.settings.isFlippedX;
	}

	public get isFlippedY(): boolean {
		return !!this.settings.isFlippedY;
	}

	public get ports() {
		const ports = ObjectUtils.deepClone(this.catalog.portTemplate);
		const labelOverrides = this.portData;

		for (const labelOverride of labelOverrides) {
			const endPoint = ports.find((c) => c.id === labelOverride.id);

			// label override can be applied to middle-layer-ports as well, not just endpoints
			if (endPoint) {
				endPoint.label = labelOverride.label;
			}
		}

		return ports;
	}

	public get id() {
		return this._data.xyiconID;
	}

	// For cut-paste
	public get misc() {
		return "";
	}

	public get refId() {
		return this._data.xyiconRefID;
	}

	public get typeId() {
		return this.catalog?.typeId ?? "";
	}

	public setSpaceId(spaceId: string) {
		this._data.spaceID = spaceId;
		for (const child of this.embeddedXyicons) {
			child.setSpaceId(spaceId);
		}
	}

	public get spaceId(): string {
		// If it has a parent, use its spaceId. In theory, embedded xyicons should be in the same space as their parents, but there might be corrupt data
		// Note that `return this.parentXyicon?.spaceId || this._data.spaceID` is wrong, because parentXyicon might be unplotted, so it's spaceId === null,
		// and in that case `this._data.spaceID` is returned
		const parentXyicon = this.parentXyicon;

		if (parentXyicon) {
			if (parentXyicon.parentXyicon === this) {
				return this._data.spaceID;
			}
		}

		return parentXyicon ? parentXyicon.spaceId : this._data.spaceID;
	}

	public get space() {
		const spaceId = this.spaceId;

		return spaceId ? this._appState.actions.getFeatureItemById<Space>(spaceId, XyiconFeature.Space) : null;
	}

	public get spaceTypeId() {
		return (this._data as OrgSearchBoundarySpaceMapDto).spaceTypeID ?? this.space?.typeId ?? "";
	}

	public get spaceName() {
		return (this._data as OrgSearchBoundarySpaceMapDto).spaceName ?? this.space?.name ?? "";
	}

	public set catalogId(value: string) {
		this._data.xyiconCatalogID = value;
	}

	public get catalogId() {
		return this._data.xyiconCatalogID;
	}

	public setPosition(x: number, y: number, z: number) {
		this._data.iconX = x;
		this._data.iconY = y;
		this._data.iconZ = z;
	}

	public setOrientation(orientation: number) {
		this._data.orientation = orientation;
	}

	public unplot() {
		this.setSpaceId(null);
	}

	public get iconX() {
		return this._data.iconX;
	}

	public get iconY() {
		return this._data.iconY;
	}

	public get iconZ() {
		return this._data.iconZ || 0;
	}

	public get position(): Dimensions {
		return {
			x: this.iconX,
			y: this.iconY,
			z: this.iconZ,
		};
	}

	public get orientation() {
		return this._data.orientation;
	}

	public get iconCategory() {
		return this._data.iconType;
	}

	public get fieldData() {
		return this._data.fieldData;
	}

	public get linkedFieldData() {
		return (this._data as OrgSearchResponseDto).linkedFieldData ?? {};
	}

	public get lastModifiedBy() {
		return this._data.lastModifiedBy;
	}

	public get lastModifiedAt() {
		return this._data.lastModifiedAt;
	}

	public get catalog() {
		const catalog = this._appState.actions.getFeatureItemById(this.catalogId, XyiconFeature.XyiconCatalog) as Catalog;

		if (!catalog) {
			console.warn(
				`Catalog doesn't exist for this xyicon: guid: ${this.id}, refId: ${this.refId}\nIt's either deleted, or maybe the catalog hasn't been loaded before we tried to access the xyicon.`,
			);
		}

		return catalog;
	}

	public get type() {
		return this._appState.actions.getTypeById(this.typeId);
	}

	public get typeName() {
		return this.type?.name || "";
	}

	private get sanitizedId() {
		// in some cases, we have some postfix after the id, eg.: "_ghost"
		return this.id.split("_")[0];
	}

	public get embeddedXyicons() {
		const itemId = this.sanitizedId;
		const xyiconsForXyicon: {link: Link; object: Xyicon}[] = this._appState.actions.getLinksXyiconXyicon(itemId);

		const embeddedXyicons: Xyicon[] = [];

		for (const obj of xyiconsForXyicon) {
			const {link, object} = obj;
			const isSelectedTypeFrom = itemId === link.objectAID;

			if (isSelectedTypeFrom && link.isEmbedded) {
				embeddedXyicons.push(object);
			}
		}

		return embeddedXyicons;
	}

	public get parentXyicon() {
		const itemId = this.sanitizedId; // in some cases, we have some postfix after the id, eg.: "_ghost"
		const xyiconsForXyicon: {link: Link; object: Xyicon}[] = this._appState.actions.getLinksXyiconXyicon(itemId);

		for (const obj of xyiconsForXyicon) {
			const {link, object} = obj;
			const isSelectedTypeTo = itemId === link.objectBID;

			if (isSelectedTypeTo && link.isEmbedded) {
				return object;
			}
		}

		return null;
	}

	public get parentXyiconRefId() {
		return this.parentXyicon?.refId || "";
	}

	public get tempRefId() {
		return this.metaData?.tempRefId ?? null; // created by copied the xyicon with this refId
	}

	private get metaData() {
		return this._data.settings?.metadata;
	}

	public get model() {
		return this.catalog?.model || "";
	}

	public get icon() {
		return this.catalog?.icon;
	}

	public get thumbnail() {
		return this.catalog?.thumbnail || "";
	}

	public get backgroundTransform(): string {
		const transforms: string[] = [];

		if (this.isFlippedX) {
			transforms.push("scaleX(-1)");
		}
		if (this.isFlippedY) {
			transforms.push("scaleY(-1)");
		}

		return transforms.join(" ");
	}

	public get isEmbedded() {
		return !!this.parentXyicon;
	}

	public get isPositionLocked() {
		return this._data.isPositionLocked;
	}

	public setPositionLocked(isLocked: boolean) {
		this._data.isPositionLocked = isLocked;
	}

	public get isExternal() {
		const activeSpaceId = this._appState.space?.id;
		const xyiconSpaceId = this.spaceId;

		return activeSpaceId && xyiconSpaceId && activeSpaceId !== xyiconSpaceId;
	}

	public get portData() {
		return this._data.portData.filter((data) => !!data.label);
	}

	public get isUnplotted() {
		return !this.spaceId;
	}

	public get data() {
		return this._data;
	}

	public get portfolioId() {
		return this._data.portfolioID;
	}

	public get spaceItemType(): SpaceItemType {
		return "xyicon";
	}
}

/**
 * The minimum necessary data to create a xyicon in space
 */
export interface IXyiconMinimumData extends XyiconDto {
	tempRefId?: string;
	tempParentXyiconId?: string;
	iconX: number;
	iconY: number;
	iconZ: number;
	orientation: number;
	catalogId: string;
	catalog?: Catalog;
	embeddedXyicons: Xyicon[];
	parentXyicon: IXyiconMinimumData | null;
	fieldData: Record<string, any> | null;
	portData: PortDataDto[];
}

interface IXyiconUpdate {
	fieldValues?: IFieldValues;
	lastModifiedAt?: string;
	lastModifiedBy?: string;
	xyiconIDList?: string[];
}

interface IFieldValues {
	[refId: string]: string;
}
